import { Card, Suit, Rank } from '../types';

const SUITS: Suit[] = ['♠', '♥', '♦', '♣'];
const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

/**
 * Seeded pseudo-random number generator using Mulberry32 algorithm.
 * This is a fast, high-quality 32-bit PRNG that produces deterministic
 * sequences from a given seed.
 * 
 * @param seed - Integer seed value
 * @returns A function that returns random numbers in [0, 1)
 */
export function createSeededRandom(seed: number): () => number {
  // Use the seed directly as our state
  let state = seed >>> 0; // Ensure unsigned 32-bit integer
  
  return function() {
    // Mulberry32 algorithm
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Generate a random seed using crypto API for high-quality randomness.
 * Falls back to Date.now() + Math.random() if crypto is unavailable.
 */
export function generateSeed(): number {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0];
  }
  // Fallback for environments without crypto
  return (Date.now() ^ (Math.random() * 0x100000000)) >>> 0;
}

function createDeck(): Card[] {
  const deck: Card[] = [];
  let idCounter = 0;
  
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        suit,
        rank,
        id: `${suit}-${rank}-${idCounter++}`,
        faceUp: false
      });
    }
  }
  
  return deck;
}

/**
 * Create a shoe with the specified number of decks.
 * 
 * @param numDecks - Number of decks in the shoe
 * @param seed - Optional seed for deterministic shuffle. If not provided, uses random shuffle.
 * @returns Object containing the shuffled deck and the seed used
 */
export function createShoe(numDecks: number, seed?: number): Card[] {
  const shoe: Card[] = [];
  
  for (let i = 0; i < numDecks; i++) {
    shoe.push(...createDeck().map(card => ({
      ...card,
      id: `${card.id}-deck${i}`
    })));
  }
  
  return shuffleDeck(shoe, seed);
}

/**
 * Create a shoe and return both the deck and the seed used.
 * This is the preferred method when you need to persist/restore deck state.
 * 
 * @param numDecks - Number of decks in the shoe
 * @param seed - Optional seed. If not provided, generates a new one.
 * @returns Object with shuffled deck and the seed used
 */
export function createSeededShoe(numDecks: number, seed?: number): { deck: Card[]; seed: number } {
  const actualSeed = seed ?? generateSeed();
  const deck = createShoe(numDecks, actualSeed);
  return { deck, seed: actualSeed };
}

/**
 * Recreate a deck from a seed and restore dealt card state.
 * This allows restoring a game session by only storing:
 * - seed (4 bytes)
 * - cardsDealt count (4 bytes)
 * Instead of the full deck array (potentially 20KB+)
 * 
 * @param numDecks - Number of decks in the shoe
 * @param seed - The seed used for the original shuffle
 * @param cardsDealtCount - Number of cards already dealt from the deck
 * @returns Object with remaining deck and dealt cards arrays
 */
export function restoreDeckFromSeed(
  numDecks: number,
  seed: number,
  cardsDealtCount: number
): { deck: Card[]; dealtCards: Card[] } {
  const fullDeck = createShoe(numDecks, seed);
  
  // The dealt cards are from the END of the deck (we pop from the end)
  // So if 10 cards were dealt from a 312-card shoe:
  // - dealtCards = last 10 cards of original shuffle
  // - remaining deck = first 302 cards
  const splitPoint = fullDeck.length - cardsDealtCount;
  const deck = fullDeck.slice(0, splitPoint);
  const dealtCards = fullDeck.slice(splitPoint);
  
  return { deck, dealtCards };
}

function shuffleDeck(deck: Card[], seed?: number): Card[] {
  const shuffled = [...deck];
  
  // Use seeded random if seed provided, otherwise use Math.random
  const random = seed !== undefined 
    ? createSeededRandom(seed) 
    : Math.random;
  
  // Fisher-Yates shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
}

function getCardNumericValue(rank: Rank): number {
  if (rank === 'A') return 11;
  if (['J', 'Q', 'K'].includes(rank)) return 10;
  return parseInt(rank);
}

export function getHandValue(cards: Card[]): { value: number; soft: boolean } {
  let value = 0;
  let aces = 0;
  
  for (const card of cards) {
    const cardValue = getCardNumericValue(card.rank);
    if (card.rank === 'A') {
      aces++;
      value += 11;
    } else {
      value += cardValue;
    }
  }
  
  // Adjust for aces
  while (value > 21 && aces > 0) {
    value -= 10;
    aces--;
  }
  
  const soft = aces > 0 && value <= 21;
  
  return { value, soft };
}

export function isBlackjack(cards: Card[]): boolean {
  if (cards.length !== 2) return false;
  const { value } = getHandValue(cards);
  return value === 21;
}

export function calculateTrueCount(runningCount: number, decksRemaining: number): number {
  if (decksRemaining <= 0) return 0;
  return Math.trunc(runningCount / decksRemaining); // truncate toward zero (standard counting)
}

export function getDecksRemaining(cardsRemaining: number): number {
  return cardsRemaining / 52; // raw; callers truncate the resulting TC
}

export function shouldReshuffle(deck: Card[], totalCards: number, penetration: number): boolean {
  const cardsDealt = totalCards - deck.length;
  const penetrationPoint = totalCards * (penetration / 100);
  return cardsDealt >= penetrationPoint;
}
